import React, { useEffect, useRef } from 'react';
import SectionShell from '../SectionShell';
import Reveal from '../Reveal';
import RevealText from '../RevealText';
import { useT } from '../../i18n/LanguageContext';

/**
 * Final CTA — replaces slide 35.  The old canvas-based fireworks are gone;
 * the section is now framed like the Pause slide and plays the Dubai
 * fireworks video looping behind the copy.
 */
export default function SectionCTA() {
  const { t, lang } = useT();
  const videoRef = useRef(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return undefined;
    // Lazy-load: only fetch the Dubai fireworks video when the section
    // is near the viewport — saves ~10 MB on initial page load.
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (v.querySelector('source') && v.readyState === 0) v.load();
            v.play().catch(() => {});
          } else {
            v.pause();
          }
        });
      },
      { rootMargin: '400px 0px', threshold: 0 },
    );
    io.observe(v);
    return () => io.disconnect();
  }, []);

  return (
    <SectionShell id="cta" className="relative overflow-hidden" fullBleed>
      <div className="cta-stage" ref={sectionRef}>
        <div className="cta-media">
          <video
            ref={videoRef}
            className="cta-video"
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            aria-hidden="true"
            data-testid="cta-video"
          >
            <source src="/videos/feuerwerk-dubai.mp4"  type="video/mp4" />
            <source src="/videos/feuerwerk-dubai.webm" type="video/webm" />
          </video>

          {/* Soft vignette so the copy stays legible against the fireworks */}
          <div className="cta-video-scrim" aria-hidden="true" />

          <div className="relative flex flex-col items-center text-center w-full max-w-none mx-auto px-6 pt-8 md:pt-12 pb-14 z-10">
            <Reveal>
              <div className="cta-glass cta-glass--overline" data-testid="cta-overline">
                <div className="overline">{t.cta.overline}</div>
              </div>
            </Reveal>
            <Reveal delay={150}>
              <div className="cta-glass cta-glass--title">
                <h2
                  data-testid="cta-title"
                  className="stig-headline font-display font-light text-white uppercase"
                >
                  {((t.cta && t.cta.title)
                    ? [t.cta.title.replace(/\.$/, '').toUpperCase()]
                    : ['THE RIGHT TIME IS NOW']
                  ).map((ln, i) => (
                    <RevealText key={`${lang}-${i}-${ln}`} as="span" delay={150 + i * 240} stagger={36} className="block">
                      {ln}
                    </RevealText>
                  ))}
                </h2>
              </div>
            </Reveal>

            <Reveal delay={300}>
              <div className="cta-glass cta-glass--body">
                <p className="cta-body-text font-ui text-white/90 leading-relaxed max-w-[60ch] font-light m-0">
                  {t.cta.body}
                </p>
              </div>
            </Reveal>

            <Reveal delay={400}>
              <div
                data-testid="cta-hint"
                className="cta-hint-box relative rounded-sm p-6 md:p-8 max-w-[60ch]"
              >
                <span className="cta-sparkle" aria-hidden>
                  <span className="cta-sparkle-star" />
                  <span className="cta-sparkle-ring" />
                </span>
                <p className="cta-hint-text font-ui text-white/95 leading-snug font-light relative z-10">
                  {t.cta.hint}
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </SectionShell>
  );
}
